import { InputAdornment, OutlinedTextFieldProps, TextField } from '@mui/material';
import React, { FC, ReactNode, useEffect } from 'react';
import { DsTextFieldExtraProps } from '../DsTextField';
import { useVoiceInputIntegration } from '../hooks/useVoiceInputIntegration';
import EndAdornment from './EndAdornment';
import StartAdornment from './StartAdornment';

const AdornmentContainer = ({ children, position, ...props }: { children: ReactNode; position: 'start' | 'end' }) => (
  <InputAdornment
    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
    position={position}
    {...props}
  >
    {children}
  </InputAdornment>
);

export interface CustomTextFieldProps {
  dsTextFieldProps: Omit<OutlinedTextFieldProps, 'variant'> & {
    inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  };
  dsTextFieldExtraProps: DsTextFieldExtraProps;

  // more props
  onClear?: VoidFunction;
  controlledValue: unknown;
  setControlledValue: React.Dispatch<unknown>;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
}

const CustomTextField: FC<CustomTextFieldProps> = ({
  controlledValue,
  setControlledValue,
  endAdornment,
  startAdornment,
  dsTextFieldProps,
  dsTextFieldExtraProps,
  onClear,
}) => {
  const hasValue = !!controlledValue || !!dsTextFieldProps.value || !!dsTextFieldProps.defaultValue;
  const hasPrefix = dsTextFieldExtraProps.prefixIcon || dsTextFieldProps.InputProps?.startAdornment || startAdornment;
  const hasSuffix = dsTextFieldExtraProps.suffixIcon || dsTextFieldProps.InputProps?.endAdornment || endAdornment;

  const [shrink, setShrink] = React.useState(!hasPrefix ? hasValue : true);

  // Use voice input context if available (will be null if not in smart zone)
  const voiceInputContext = useVoiceInputIntegration();

  // for multiple reasons we have to handle input shrink manually
  // if input had prefix icon shrink must be true
  // if input had value shrink must be true
  useEffect(() => {
    setShrink(!hasPrefix ? hasValue : true);
  }, [hasValue, hasPrefix]);

  useEffect(() => {
    // we handle value change of tell inside tell input itself
    if (dsTextFieldProps.type === 'tel') return;
    setControlledValue(dsTextFieldProps.value);
  }, [dsTextFieldProps.value]);

  // Voice input handlers
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setShrink(true);

    // Register with voice input context if available
    if (voiceInputContext && dsTextFieldProps.inputRef?.current) {
      voiceInputContext.registerInput(dsTextFieldProps.inputRef.current);
    } else if (voiceInputContext && e.target) {
      // Fallback: use the event target directly if inputRef is not available
      voiceInputContext.registerInput(e.target as HTMLInputElement | HTMLTextAreaElement);
    }

    // Call original onFocus if provided
    if (dsTextFieldProps.onFocus) {
      dsTextFieldProps.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setShrink(!hasPrefix ? hasValue : true);

    // Unregister from voice input context if available
    if (voiceInputContext) {
      voiceInputContext.unregisterInput();
    }

    // Call original onBlur if provided
    if (dsTextFieldProps.onBlur) {
      dsTextFieldProps.onBlur(e);
    }
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;

    // Update caret position in voice input context if available
    if (voiceInputContext && target.selectionStart !== null && target.selectionEnd !== null) {
      voiceInputContext.updateCaretPosition(target.selectionStart, target.selectionEnd);
    }

    // Call original onSelect if provided
    if (dsTextFieldProps.onSelect) {
      dsTextFieldProps.onSelect(e as any);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;

    // Update caret position when clicking in the input
    setTimeout(() => {
      if (voiceInputContext && target.selectionStart !== null && target.selectionEnd !== null) {
        voiceInputContext.updateCaretPosition(target.selectionStart, target.selectionEnd);
      }
    }, 0);

    // Call original onClick if provided
    if (dsTextFieldProps.onClick) {
      dsTextFieldProps.onClick(e as any);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;

    // Update caret position after keyboard input
    if (voiceInputContext && target.selectionStart !== null && target.selectionEnd !== null) {
      voiceInputContext.updateCaretPosition(target.selectionStart, target.selectionEnd);
    }

    // Call original onKeyUp if provided
    if (dsTextFieldProps.onKeyUp) {
      dsTextFieldProps.onKeyUp(e as any);
    }
  };

  return (
    <TextField
      {...dsTextFieldProps}
      inputRef={dsTextFieldProps.inputRef}
      sx={{
        ...dsTextFieldProps.sx,
        ...(dsTextFieldExtraProps.prefixIcon?.onClick && {
          '& .MuiOutlinedInput-root': {
            paddingLeft: '0px !important',
          },
        }),
        '& .MuiOutlinedInput-root': {
          ...(hasSuffix && {
            paddingRight: '0 !important',
          }),
          ...(hasPrefix && {
            paddingLeft: '0 !important',
          }),
        },
      }}
      value={controlledValue || dsTextFieldProps.value || ''}
      onChange={dsTextFieldProps.onChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onSelect={handleSelect as any}
      onClick={handleClick as any}
      onKeyUp={handleKeyUp as any}
      InputLabelProps={{ shrink }}
      variant="outlined"
      InputProps={{
        ...dsTextFieldProps.InputProps,
        startAdornment: (
          <AdornmentContainer position="start">
            {dsTextFieldProps.InputProps?.startAdornment}
            <StartAdornment
              controlledValue={controlledValue}
              setControlledValue={setControlledValue}
              {...dsTextFieldExtraProps}
              {...dsTextFieldProps}
            />
            {startAdornment}
          </AdornmentContainer>
        ),
        endAdornment: (
          <AdornmentContainer position="end">
            {endAdornment}
            <EndAdornment
              controlledValue={controlledValue}
              setControlledValue={setControlledValue}
              onClear={onClear}
              {...dsTextFieldExtraProps}
              {...dsTextFieldProps}
            />
            {dsTextFieldProps.InputProps?.endAdornment}
          </AdornmentContainer>
        ),
      }}
    />
  );
};

export default CustomTextField;
